<template>
  <div
    v-if="show"
    class="modal d-block"
    style="background-color: rgba(0, 0, 0, 0.5);"
    tabindex="-1"
    @mousedown.self="onCancel"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content shadow">

        <div class="modal-header py-2">
          <h6 class="modal-title mb-0">
            <i class="fas fa-link me-2 text-muted"></i>Insert / edit link
          </h6>
          <button type="button" class="btn-close" @click="onCancel"></button>
        </div>

        <div class="modal-body">

          <div class="mb-3">
            <label class="form-label small fw-semibold">URL</label>
            <div class="d-flex gap-2 align-items-start">
              <input
                v-model="form.href"
                type="text"
                class="form-control form-control-sm"
                placeholder="https://..."
              />
              <badiu-tiptap-file-upload
                accept="*/*"
                filetype="file"
                title="Upload file and use URL"
                icon="fa-paperclip"
                :config="config"
                @uploaded="onUploaded"
                @error="onUploadError"
              />
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label small fw-semibold">Display text</label>
            <input
              v-model="form.text"
              type="text"
              class="form-control form-control-sm"
              placeholder="Link text"
            />
          </div>

          <div class="mb-3">
            <label class="form-label small fw-semibold">Title (tooltip)</label>
            <input
              v-model="form.title"
              type="text"
              class="form-control form-control-sm"
              placeholder="Shown on hover"
            />
          </div>

          <div class="mb-1">
            <label class="form-label small fw-semibold">Open in</label>
            <select v-model="form.target" class="form-select form-select-sm">
              <option value="">Same window (default)</option>
              <option value="_blank">New window (_blank)</option>
            </select>
          </div>

        </div>

        <div class="modal-footer py-2">
          <button
            v-if="hasExistingLink"
            type="button"
            class="btn btn-outline-danger btn-sm me-auto"
            @click="onRemove"
          >
            <i class="fas fa-unlink me-1"></i>Remove link
          </button>

          <button
            type="button"
            class="btn btn-secondary btn-sm"
            @click="onCancel"
          >
            Cancel
          </button>

          <button
            type="button"
            class="btn btn-primary btn-sm"
            :disabled="!form.href"
            @click="onSave"
          >
            Save
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
/**
 * BadiuTiptapLink
 *
 * Isolated modal component for inserting and editing links — TinyMCE-style:
 *  - URL (with optional file upload)
 *  - Display text
 *  - Title (tooltip)
 *  - Target (same window / _blank)
 *  - Remove existing link
 */
import { reactive, computed, watch } from 'vue'
import BadiuTiptapFileUpload from './BadiuTiptapFileUpload.vue'

export default {
  name: "BadiuTiptapLink",

  components: {
    BadiuTiptapFileUpload
  },

  props: {
    show: {
      type: Boolean,
      default: false
    },

    initial: {
      type: Object,
      default: () => ({})
    },

    /** Forwarded to BadiuTiptapFileUpload (ver BadiuTiptapEditor para doc das chaves). */
    config: {
      type: Object,
      default: () => ({})
    }
  },

  emits: ["save", "remove", "cancel", "uploaded", "error"],

  setup(props, { emit }) {
    const form = reactive({
      href:   "",
      text:   "",
      title:  "",
      target: ""
    });

    const hasExistingLink = computed(() => !!props.initial.href);

    watch(
      () => props.show,
      (visible) => {
        if (visible) {
          form.href   = props.initial.href   || "";
          form.text   = props.initial.text   || "";
          form.title  = props.initial.title  || "";
          form.target = props.initial.target || "";
        }
      },
      { immediate: true }
    );

    function onSave() {
      if (!form.href) return;

      emit("save", {
        href:   form.href,
        text:   form.text || form.href,
        title:  form.title  || null,
        target: form.target || null
      });
    }

    function onRemove() {
      emit("remove");
    }

    function onCancel() {
      emit("cancel");
    }

    function onUploaded(payload) {
      form.href = payload.url;

      if (!form.text && payload.name) {
        form.text = payload.name;
      }

      emit("uploaded", payload);
    }

    function onUploadError(payload) {
      emit("error", payload);
    }

    return {
      form,
      hasExistingLink,
      onSave,
      onRemove,
      onCancel,
      onUploaded,
      onUploadError
    };
  }
};
</script>
